"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import { api } from "@/shared/lib/api";
import { type PodiumEntry } from "../tournament-podium";
import { TournamentResultsSections } from "@/widgets/tournament-results/tournament-results-sections";
import {
  liveTeamsToRail,
  type LiveTournamentTeam,
} from "@/widgets/tournament-results/lib/live-teams-to-rail";
import { TournamentChampionStatsCard } from "../tournament-champion-stats-card";
import { TournamentStatsCoverageNote } from "../tournament-stats-coverage-note";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { DeferredMount } from "@/shared/ui/layout/deferred-mount";
import { useBracket } from "@/entities/bracket/api/hooks";
import { useIsTournamentFinished } from "@/entities/tournament/model/use-is-tournament-finished";
import { convertDomainToBracketData } from "@/entities/bracket/lib/domain-convert";
import { BracketBoard } from "@/widgets/bracket/bracket-board";
import { ViewerBracketView } from "@/widgets/bracket/viewer-view.dynamic";
import { tournamentKeys } from "@/shared/api/query-keys";
import { QUERY_VOLATILITY } from "@/shared/config/api-constants";

/**
 * Ответ `/tournaments/{id}/results`. Тип РУЧНОЙ: ручки нет в OpenAPI, потому что
 * `ProducesResponseType` объявлен без `typeof` (KAN-873).
 *
 * KAN-891 — цена этого ровно здесь. Бэкенд отдавал `role` с KAN-806, но в типе поля
 * не было, и подиум на странице показывал состав без иконок позиций. Компиляция
 * молчала: у необъявленного поля нет ошибки, есть `undefined`. При типизации ручки
 * этот тип удаляется целиком, а не правится.
 */
type PodiumMemberDto = {
  userId?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  role?: string | null;
};

type TeamResultDto = {
  teamId: string;
  name: string;
  tag?: string | null;
  icon?: string | null;
  wins: number;
  losses: number;
  captainId?: string | null;
  members?: PodiumMemberDto[] | null;
};

type PodiumPlaceDto = {
  place: number;
  teams: TeamResultDto[];
};

type TournamentResultsResponse = {
  tournamentId: string;
  isCompleted: boolean;
  podium: PodiumPlaceDto[];
};

async function fetchTournamentResults(tournamentId: string) {
  const res = await api.get<TournamentResultsResponse>(
    `/tournaments/${encodeURIComponent(tournamentId)}/results`,
  );
  return res.data;
}

/** Нормализует podium из /results в PodiumEntry[] (общая логика для live/архива). */
export function buildPodiumEntries(
  podium: PodiumPlaceDto[] | undefined,
): PodiumEntry[] {
  return (podium ?? [])
    .slice()
    .sort((a, b) => a.place - b.place)
    .flatMap((placeData) =>
      (placeData.teams ?? []).map(
        (pt) =>
          ({
            place: placeData.place,
            name: pt.name,
            tag: pt.tag,
            score: `${pt.wins} : ${pt.losses}`,
            captainId: pt.captainId,
            members: (pt.members ?? []).map((m) => ({
              userId: m.userId,
              displayName: m.displayName,
              avatarUrl: m.avatarUrl,
              role: m.role,
            })),
          }) satisfies PodiumEntry,
      ),
    );
}

export function TournamentResultsTab({
  tournamentId,
}: {
  tournamentId: string;
  tournamentRegion?: string | null;
}) {
  const t = useTranslations("Tournaments");
  // /results может падать на турнирах с неконсистентными данными (legacy).
  // Не блокируем весь таб — подиум показываем если данные есть, статистику —
  // всегда (у виджетов свои empty-states). retry:false чтобы не долбить 500.
  const { data, isLoading } = useQuery({
    queryKey: tournamentKeys.results(tournamentId),
    queryFn: () => fetchTournamentResults(tournamentId),
    enabled: Boolean(tournamentId),
    ...QUERY_VOLATILITY.LIVE,
    retry: false,
  });

  // Пройденная сетка прямо на итогах: турнир завершён, но ещё не заархивирован,
  // и гонять читателя на соседнюю вкладку за результатом матчей незачем.
  const isFinished = useIsTournamentFinished(tournamentId);
  const { data: apiBracket } = useBracket(tournamentId, { isFinished });
  const bracketData = useMemo(
    () => (apiBracket ? convertDomainToBracketData(apiBracket) : null),
    [apiBracket],
  );

  // KAN-878: составы для ленты. У живого турнира они в отдельной ручке — архив
  // берёт их из замороженного снимка. Приводим к общему виду адаптером, но НЕ
  // подставляем сегодняшние ранг и твич вместо турнирных: их у живой ручки нет по
  // существу, а подстановка выдала бы «как сейчас» за «как было».
  const { data: liveTeams } = useQuery({
    queryKey: ["tournaments", tournamentId, "teams"],
    queryFn: async () => {
      const res = await api.get<LiveTournamentTeam[]>(
        `/tournaments/${encodeURIComponent(tournamentId)}/teams`,
      );
      return res.data ?? [];
    },
    enabled: Boolean(tournamentId),
    ...QUERY_VOLATILITY.STANDARD,
  });

  const podiumEntries = buildPodiumEntries(data?.podium);
  const hasPodium = podiumEntries.length > 0;

  // Место и счёт по картам берём из тех же результатов, что и пьедестал: второй
  // источник развёл бы цифры между лентой и пьедесталом.
  const railTeams = useMemo(() => {
    const placement: Record<string, { placement?: number | null; wins: number; losses: number }> = {};
    for (const place of data?.podium ?? []) {
      for (const team of place.teams ?? []) {
        placement[team.teamId] = {
          placement: place.place,
          wins: team.wins,
          losses: team.losses,
        };
      }
    }
    return liveTeamsToRail(liveTeams, placement);
  }, [liveTeams, data?.podium]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* KAN-878: те же секции, что и на архивной странице. Раньше здесь была своя
          сборка из старых блоков, и после редизайна живой турнир выглядел беднее
          собственного архива — жалоба KAN-787 наоборот. */}
      <TournamentResultsSections
        tournamentId={tournamentId}
        podiumEntries={hasPodium ? podiumEntries : undefined}
        teams={railTeams}
        podiumTitle={t("resultsTab.title")}
      />

      {/* Ниже итогов — монтаж на подходе к экрану, как в архиве (KAN-1243). */}
      <DeferredMount minHeight={420}>
      {bracketData ? (
        <div className="rounded-[14px] border border-border bg-card p-6 shadow-sm overflow-x-auto">
          <div className="mb-4 font-heading text-xl font-bold">
            {t("bracketTab.title")}
          </div>
          {/* Тот же рендер, что и на вкладке «Турнирная сетка»
              (tournament-bracket-tab.tsx) — сетку сюда добавили тем же
              способом в тот же день, но забыли завести на новый BracketBoard.
              Swiss/round-robin BracketBoard не умеет (падает в SE-фолбэк),
              поэтому для них — ViewerBracketView, как и на живой вкладке.
              Итоги — только просмотр: onMatchClick/manage не передаём. */}
          {bracketData.format === "swiss" ||
          bracketData.format === "round_robin" ? (
            <ViewerBracketView data={bracketData} />
          ) : (
            <BracketBoard
              data={bracketData}
              teamsLookup={Object.fromEntries(
                bracketData.teams.map((t) => [t.id, t]),
              )}
            />
          )}
        </div>
      ) : null}
      </DeferredMount>

      <TournamentStatsCoverageNote tournamentId={tournamentId} />
      <DeferredMount minHeight={320}>
        <TournamentChampionStatsCard tournamentId={tournamentId} />
      </DeferredMount>
      {/* KAN-792: командный срез турнира — отдельная витрина рядом с поимённой.
          Все величины «за игру»: суммы за турнир врали бы в пользу тех, кто дошёл
          дальше по сетке. Блок сам себя прячет, если реплеев нет. */}
    </div>
  );
}
